import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listsApi } from '../utils/listsApi';
import './BulkCollectionActions.css';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const PREVIEW_KEY = 'faunatrack-temporary-intersection';
const MAX_INTERSECTION_ANIMALS = 50;

// Used by Home and Explore when "Select Many" is enabled.
function BulkCollectionActions({ selectedIds, onClear }) {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [destination, setDestination] = useState('favorites');
  const [loadingLists, setLoadingLists] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const token = localStorage.getItem('token');
  const selectedSignature = selectedIds.join(',');

  useEffect(() => {
    setNotice('');
    setError('');
  }, [selectedSignature]);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setLoadingLists(true);

    listsApi('/lists')
      .then(data => {
        if (active) setLists(data);
      })
      .catch(err => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoadingLists(false);
      });

    return () => { active = false; };
  }, [token]);

  async function handleAdd() {
    if (!token || saving || selectedIds.length === 0) return;
    setSaving(true);
    setError('');
    setNotice('');

    try {
      let data;
      if (destination === 'favorites') {
        const response = await fetch(`${API_URL}/favorites/bulk`, {
          method: 'POST',
          headers: {
            token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ animal_ids: selectedIds })
        });

        data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(data?.error || 'Unable to add selected animals.');
        }
      } else {
        data = await listsApi(`/lists/${destination}/animals/bulk`, {
          method: 'POST',
          body: { animal_ids: selectedIds }
        });
      }

      setNotice(
        `${data.added} added; ${data.already_present} already saved.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Unlike Favorites and custom lists, this works without an account.
  function handleIntersection() {
    if (selectedIds.length < 2 || selectedIds.length > MAX_INTERSECTION_ANIMALS) {
      return;
    }

    try {
      sessionStorage.setItem(
        PREVIEW_KEY,
        JSON.stringify([...new Set(selectedIds)])
      );
      navigate('/intersection-preview');
    } catch {
      setError('Your browser could not store the temporary selection.');
    }
  }

  return (
    <div className="bulk-collections">
      <div className="bulk-collections__top">
        <strong>{selectedIds.length} selected</strong>
        <div className="bulk-collections__quick-actions">
          <button
            type="button"
            className="bulk-collections__intersection"
            onClick={handleIntersection}
            disabled={saving || selectedIds.length < 2 ||
              selectedIds.length > MAX_INTERSECTION_ANIMALS}
          >
            Check Intersection
          </button>
          <button type="button" onClick={onClear} disabled={saving}>
            Clear selection
          </button>
        </div>
      </div>

      {selectedIds.length > MAX_INTERSECTION_ANIMALS && (
        <p className="bulk-collections__limit" role="status">
          Intersection supports up to 50 animals. Deselect some to continue.
        </p>
      )}

      {!token ? (
        <p>
          <Link to="/login">Log in</Link> or{' '}
          <Link to="/register">sign up</Link> to save animals.
        </p>
      ) : (
        <div className="bulk-collections__controls">
          <label htmlFor="bulk-destination">Add to</label>
          <select
            id="bulk-destination"
            value={destination}
            disabled={loadingLists || saving}
            onChange={event => setDestination(event.target.value)}
          >
            <option value="favorites">Favorites</option>
            {lists.map(list => (
              <option key={list.id} value={String(list.id)}>
                {list.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="bulk-collections__save"
            onClick={handleAdd}
            disabled={saving || selectedIds.length === 0 || loadingLists}
          >
            {saving ? 'Saving...' : 'Add selected'}
          </button>
        </div>
      )}

      {error && <p className="bulk-collections__error" role="alert">{error}</p>}
      {notice && <p className="bulk-collections__notice" role="status">{notice}</p>}
    </div>
  );
}

export default BulkCollectionActions;
